import { useAuth } from '@core/context/AuthContext';
import { useInventoryState } from '@core/context/InventoryContext';
import { 
    collection, 
    doc, 
    updateDoc, 
    increment, 
    addDoc, 
    serverTimestamp,
    deleteDoc,
    runTransaction,
    writeBatch,
    db 
} from '@core/firebase';
import { generateSearchKeywords } from '@core/utils/searchUtils';
import { useCallback } from 'react';

/**
 * useInventory Hook - Consumers the Global Inventory Context.
 * Provides mutation methods (Add, Update, Delete, Withdraw).
 */
export const useInventory = () => {
    const { products, loading, error } = useInventoryState();
    const { user } = useAuth();

    const registerMovement = useCallback(async (product, qty, type, note = '') => {
        const productRef = doc(db, 'inventory', product.id);
        const amount = Number(qty);
        await updateDoc(productRef, { stock_current: increment(amount), updatedAt: serverTimestamp() });
        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: product.sku || product.id,
            product_name: product.name || '',
            product_description: product.description,
            product_category: product.category || 'General',
            location: product.location?.section || 'General',
            type: type === 'IN' ? 'ENTRY' : (type === 'OUT' ? 'EXIT' : type), // Normalize to ENTRY/EXIT
            amount: Math.abs(amount),
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note
        });
    }, [user]);

    const updateThresholds = useCallback(async (id, min) => {
        const productRef = doc(db, 'inventory', id);
        await updateDoc(productRef, { 
            stock_min: Number(min), 
            updatedAt: serverTimestamp() 
        });
    }, [user]);

    const updateProduct = useCallback(async (id, data) => {
        const { unit_cost, ...dataToUpdate } = data;
        const productRef = doc(db, 'inventory', id);
        await updateDoc(productRef, {
            ...dataToUpdate,
            search_keywords: generateSearchKeywords(dataToUpdate),
            updatedAt: serverTimestamp()
        });

        // Audit Log for Metadata Update
        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: data.sku || id,
            product_name: data.name || '',
            product_description: data.description || 'N/A',
            product_category: data.category || 'General',
            type: 'METADATA_UPDATE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: `Product details updated by admin`
        });
    }, [user]);

    const addProduct = useCallback(async (data) => {
        // Normalize SKU as explicit string
        const targetSku = String(data.sku || '').trim().toUpperCase();

        // ── GUARD: SKU must be unique ──────────────────────────────────────
        if (!targetSku) throw new Error('SKU is required.');
        const skuAlreadyExists = products.some(
            p => String(p.sku || '').trim().toUpperCase() === targetSku
        );
        if (skuAlreadyExists) {
            throw new Error(`SKU "${targetSku}" already exists. Each item must have a unique Part Code.`);
        }
        // ──────────────────────────────────────────────────────────────────

        const { unit_cost, ...dataToAdd } = data;

        // ADDING new document
        const productRefBuffer = await addDoc(collection(db, 'inventory'), {
            ...dataToAdd,
            isActive: true,
            sku: targetSku,
            description: data.description,
            category: data.category,
            categoryId: data.categoryId || '',
            unit: data.unit || 'PCS',
            location: {
                section: data.location?.section || 'General',
                sectionId: data.location?.sectionId || '',
                aisle: data.location?.aisle || '',
                aisleId: data.location?.aisleId || '',
                rack: data.location?.rack || '',
                rackId: data.location?.rackId || '',
                container: data.location?.container || 'Shelf'
            },
            stock_current: Number(data.stock_current || 0),
            stock_min: Number(data.stock_min || 0),
            search_keywords: generateSearchKeywords(dataToAdd),
            updatedAt: serverTimestamp()
        });

        // Single audit log for product creation (the CREATE record already carries the initial stock)
        const initialStock = Number(data.stock_current || 0);
        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: targetSku,
            product_name: data.name || '',
            product_description: data.description,
            product_category: data.category || 'General',
            type: 'CREATE',
            amount: initialStock,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: initialStock > 0
                ? `New item registered · Initial balance: ${initialStock} units`
                : 'New item registered · No initial stock'
        });
    }, [user, products, updateProduct]);

    const deleteProduct = useCallback(async (id) => {
        const productRef = doc(db, 'inventory', id);
        const product = products.find(p => p.id === id);
        
        await updateDoc(productRef, { 
            status: 'ARCHIVED', 
            updatedAt: serverTimestamp() 
        });

        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: product?.sku || id,
            product_name: product?.name || '',
            product_description: product?.description || 'N/A',
            product_category: product?.category || 'General',
            type: 'ARCHIVE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: 'Product moved to archive'
        });
    }, [user, products]);

    const deleteMultipleProducts = useCallback(async (ids) => {
        const batch = writeBatch(db);
        ids.forEach(id => {
            batch.update(doc(db, 'inventory', id), { 
                status: 'ARCHIVED', 
                updatedAt: serverTimestamp() 
            });
        });
        await batch.commit();

        // Single log entry for bulk archive
        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: 'BULK',
            product_description: `Archived ${ids.length} items`,
            type: 'BULK_ARCHIVE',
            amount: ids.length,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: `Bulk archive operation`
        });
    }, [user, products]);

    /**
     * bulkWithdrawal - Atomic transaction for multiple items.
     * Optimized for MEMBER Self-Service Terminal.
     */
    const bulkWithdrawal = useCallback(async (withdrawals, member) => {
        try {
            await runTransaction(db, async (transaction) => {
                // PHASE 1: COLLECT ALL READS
                // Firestore requires all reads to happen before ANY writes.
                const productSnapshots = [];
                for (const item of withdrawals) {
                    const productRef = doc(db, 'inventory', item.id);
                    const productDoc = await transaction.get(productRef);
                    if (!productDoc.exists()) throw new Error(`Product SKU ${item.sku} not found`);
                    productSnapshots.push({ ref: productRef, doc: productDoc, item });
                }

                // PHASE 2: EXECUTE ALL WRITES
                for (const { ref, doc: productDoc, item } of productSnapshots) {
                    const currentStock = productDoc.data().stock_current || 0;
                    const newStock = currentStock - Number(item.qty);
                    
                    if (newStock < 0) {
                        throw new Error(`Insufficient stock for ${item.sku}: Available ${currentStock}, Requested ${item.qty}`);
                    }

                    // Update Inventory
                    transaction.update(ref, { 
                        stock_current: newStock, 
                        updatedAt: serverTimestamp() 
                    });

                    // Create Movement Log
                    const logRef = doc(collection(db, 'logs'));
                    transaction.set(logRef, {
                        timestamp: serverTimestamp(),
                        product_sku: item.sku || item.id,
                        product_name: item.name || '',
                        product_description: item.description,
                        product_category: item.category || productDoc.data().category || 'General',
                        location: productDoc.data().location?.section || 'General',
                        type: 'EXIT',
                        amount: Number(item.qty),
                        user_email: member.email,
                        member_name: member.displayName,
                        member_code: member.withdrawalCode,
                        note: 'Self-service withdrawal'
                    });
                }
            });
            return { success: true };
        } catch (error) {
            console.error("Bulk withdrawal failed:", error);
            throw error;
        }
    }, [user]);

    /**
     * bulkReplenishment - Atomic transaction for multiple items (Inventory Entry).
     * Optimized for ADMIN stock replenishment.
     */
    const bulkReplenishment = useCallback(async (entries, adminUser) => {
        try {
            await runTransaction(db, async (transaction) => {
                const productSnapshots = [];
                for (const item of entries) {
                    const productRef = doc(db, 'inventory', item.id);
                    const productDoc = await transaction.get(productRef);
                    if (!productDoc.exists()) throw new Error(`Product SKU ${item.sku} not found`);
                    productSnapshots.push({ ref: productRef, doc: productDoc, item });
                }

                for (const { ref, doc: productDoc, item } of productSnapshots) {
                    const currentStock = productDoc.data().stock_current || 0;
                    const newStock = currentStock + Number(item.qty);

                    transaction.update(ref, { 
                        stock_current: newStock, 
                        updatedAt: serverTimestamp() 
                    });

                    const logRef = doc(collection(db, 'logs'));
                    transaction.set(logRef, {
                        timestamp: serverTimestamp(),
                        product_sku: item.sku || item.id,
                        product_name: item.name || '',
                        product_description: item.description,
                        product_category: item.category || productDoc.data().category || 'General',
                        location: productDoc.data().location?.section || 'General',
                        type: 'ENTRY',
                        amount: Number(item.qty),
                        user_email: adminUser.email,
                        user_name: adminUser.displayName,
                        note: 'Stock replenishment (Terminal)'
                    });
                }
            });
            return { success: true };
        } catch (error) {
            console.error("Bulk replenishment failed:", error);
            throw error;
        }
    }, [user]);

    const hardDeleteProduct = useCallback(async (id) => {
        const product = products.find(p => p.id === id);
        await deleteDoc(doc(db, 'inventory', id));

        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: product?.sku || id,
            product_name: product?.name || '',
            product_description: product?.description || 'N/A',
            product_category: product?.category || 'General',
            type: 'DELETE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: 'Product permanently destroyed from system'
        });
    }, [user, products]);

    const toggleProductStatus = useCallback(async (id, currentStatus) => {
        const newStatus = !currentStatus;
        const product = products.find(p => p.id === id);
        await updateDoc(doc(db, 'inventory', id), {
            isActive: newStatus,
            updatedAt: serverTimestamp()
        });

        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: product?.sku || id,
            product_name: product?.name || '',
            product_description: product?.description || 'N/A',
            product_category: product?.category || 'General',
            type: 'STRUCTURAL_CHANGE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: `Product ${newStatus ? 'activated' : 'deactivated'} in system`
        });
    }, [user, products]);

    return { products, loading, error, registerMovement, updateThresholds, addProduct, updateProduct, deleteProduct, deleteMultipleProducts, hardDeleteProduct, toggleProductStatus, bulkWithdrawal, bulkReplenishment };
};
